import { authenticatedClient } from '../lib/client';
import { USER_ENDPOINTS } from './users.utils';
import type { ApiResponse } from '../lib/types';
import type { User, UpdateProfilePayload, SwitchOrganizationResponse, UserDetails, AddTeamMembersResponse, AddTeamMemberRequest, UpdateMemberRequest, ResendInvitePayload } from './users.types';
import { setAuthTokens } from '../lib/config';

export const getUsers = async (): Promise<ApiResponse<UserDetails[]>> => {
    try {
        const response = await authenticatedClient.get(USER_ENDPOINTS.GET_USERS);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to fetch users' };
    }
};

export const updateProfile = async (payload: UpdateProfilePayload): Promise<ApiResponse<User>> => {
    try {
        const response = await authenticatedClient.put(USER_ENDPOINTS.UPDATE_PROFILE, payload);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to update profile' };
    }
};

export const deleteProfile = async (): Promise<ApiResponse> => {
    try {
        await authenticatedClient.delete(USER_ENDPOINTS.DELETE_PROFILE);
        return { message: 'Profile deleted successfully' };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to delete profile' };
    }
};

export const updateMember = async (data: UpdateMemberRequest): Promise<ApiResponse> => {
    try {
        const response = await authenticatedClient.put(USER_ENDPOINTS.UPDATE_MEMBER, data);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to update member' };
    }
};

export const addMembers = async (members: AddTeamMemberRequest[]): Promise<ApiResponse<AddTeamMembersResponse>> => {
    try {
        const response = await authenticatedClient.post(USER_ENDPOINTS.ADD_MEMBER, members);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to add members' };
    }
};

export const removeMember = async (id: string): Promise<ApiResponse> => {
    try {
        await authenticatedClient.delete(USER_ENDPOINTS.REMOVE_MEMBER, {
            data: { user_id: id },
            validateStatus: (status) => {
                // Consider both 200 and 204 as successful responses
                return status === 200 || status === 204;
            }
        });
        // For 204 responses, there's no data, so we create our own success message
        return { message: 'Member deleted successfully' };
    } catch (error: any) {
        console.error('Remove member error:', error);
        return { error: error.response?.data?.message || 'Failed to delete member' };
    }
};

export const getOrganizations = async (): Promise<ApiResponse> => {
    try {
        const response = await authenticatedClient.get(USER_ENDPOINTS.GET_ORGANIZATIONS);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to fetch organizations' };
    }
};

export const switchOrganization = async (organizationId: string): Promise<ApiResponse<SwitchOrganizationResponse>> => {
    try {
        const response = await authenticatedClient.get(USER_ENDPOINTS.SWITCH_ORGANIZATION(organizationId));
        const { access_token: access_token, refresh_token: refresh_token } = response.data;
        // console.log('Storing tokens:', { access_token, refresh_token });
        setAuthTokens(access_token, refresh_token);
        return { data: { access_token, refresh_token } };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to switch organization' };
    }
};

export const resendInvite = async (payload: ResendInvitePayload): Promise<ApiResponse> => {
    try {
        const response = await authenticatedClient.post(USER_ENDPOINTS.RESEND_INVITE, payload);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to resend invite' };
    }
};

